import { makeObservable, observable, action } from "mobx";
import axios from "axios";
import {
  compressJson,
  decompressJson,
  jsonCopy,
  getObjectSize,
  // getAsJson,
  // getJsonFromCompression,
  isEditorElement,
  getAsJson,
} from "../utils/helper";
import {
  getOptimizedCellAddress,
  getSheetNameFromAddress,
} from "../utils/helperExcel";

export class StoreCache {
  isLoaded = false;
  useLocalStorageForCache = true;
  latestCacheData = null;
  latestCacheDataJson = null;

  constructor(rootStore) {
    this.rootStore = rootStore;
    this.lastUpdate = new Date();

    makeObservable(this, {
      isLoaded: observable,
      setIsLoaded: action,
    });
  }

  setIsLoaded(loaded) {
    this.isLoaded = loaded;
  }

  // =========================================================
  // STORAGE
  // =========================================================

  getStorageCacheItem(storageKey) {
    if (this.useLocalStorageForCache) {
      return localStorage.getItem(storageKey);
    } else {
      return sessionStorage.getItem(storageKey);
    }
  }

  isThereAStorageCacheData(elementId) {
    // is it in storage - look for cache data
    let storageKey = "cachedata-" + elementId + "-compressed";
    if (this.useLocalStorageForCache) {
      return localStorage.hasOwnProperty(storageKey);
    } else {
      return sessionStorage.hasOwnProperty(storageKey);
    }
  }

  isThereAStorageWorkbookData(workbookId) {
    // is it in storage - look for workbook data - workbooks are always stored in session storage
    let storageKey = "workbook-" + workbookId + "-compressed";
    return sessionStorage.hasOwnProperty(storageKey);
  }

  getWorkbookDataFromStorage(workbookId) {
    let data;
    try {
      let compressedData = sessionStorage.getItem(
        "workbook-" + workbookId + "-compressed",
      );
      if (compressedData) {
        data = decompressJson(compressedData);
      }
    } catch (error) {
      console.log(
        "ERROR getWorkbookDataFromStorage - loading data from session storage",
        error,
      );
      data = null;
    }
    return data;
  }

  getCachedDataFromStorage(elementId, workbookId) {
    // is it in storage - look for workbook data
    let storageKey = "cachedata-" + elementId + "-compressed";
    let compressedData = this.getStorageCacheItem(storageKey);
    // if there is no cache - try the workbook
    if (!compressedData) {
      storageKey = "workbook-" + workbookId + "-compressed";
      compressedData = sessionStorage.getItem(storageKey);
    }
    if (!compressedData) return;

    let data;
    try {
      data = decompressJson(compressedData);
    } catch (error) {
      console.log(
        "ERROR getCachedDataFromStorage - loading data from storage",
        error,
      );
      data = null;
    }
    return data;
  }

  async writeWorkbookDataToStorage(workbookId, data) {
    try {
      let sizeFull = getObjectSize(data);
      if (sizeFull > 10000000) {
        console.log(
          "INFO writeWorkbookDataToStorage: data is larger than 10 MB, ",
          sizeFull,
        );
        return;
      }

      let compressed = compressJson(data);
      let size = getObjectSize(compressed);
      console.log("INFO writeWorkbookDataToStorage cachData size", size);
      size < 3000000 &&
        sessionStorage.setItem(
          "workbook-" + workbookId + "-compressed",
          compressed,
        );
      return;
    } catch (err) {
      console.log("ERROR writeWorkbookDataToStorage", err);
    }
  }

  async clearCacheDataFromStorage() {
    // clear session storage
    for (const key in sessionStorage) {
      // Skip built-in properties like length, setItem, etc.
      if (sessionStorage.hasOwnProperty(key)) {
        if (key.includes("cachedata-")) {
          console.log(`delete session storage ${key}`);
          sessionStorage.removeItem(key);
        }
        if (key.includes("rangedata-")) {
          console.log(`delete session storage ${key}`);
          sessionStorage.removeItem(key);
        }
        if (key.includes("workbook-")) {
          console.log(`delete session storage ${key}`);
          sessionStorage.removeItem(key);
        }
      }
    }
    // clear local storage
    for (const key in localStorage) {
      // Skip built-in properties like length, setItem, etc.
      if (localStorage.hasOwnProperty(key)) {
        if (key.includes("cachedata-")) {
          console.log(`delete local storage ${key}`);
          localStorage.removeItem(key);
        }
        if (key.includes("workbook-")) {
          console.log(`delete local storage ${key}`);
          localStorage.removeItem(key);
        }
      }
    }
  }

  // =========================================================
  // Range Data
  // =========================================================

  getRangeDefinitionFromStorage(elementId) {
    // is it in storage - look for workbook data
    let storageKey = "rangeedata-" + elementId + "-compressed";
    let compressedData = this.getStorageCacheItem(storageKey);

    if (!compressedData) return;

    let data;
    try {
      data = decompressJson(compressedData);
    } catch (error) {
      console.log(
        "ERROR getRangeDefinitionFromStorage - loading data from storage",
        error,
      );
      data = null;
    }
    return data;
  }

  // =========================================================
  // OPTIMIZATION
  // =========================================================

  async optimizeJson(data, elementId, elementList) {
    if (!data) return;

    let sizeBefore = getObjectSize(data);
    let elements = elementList ?? [];
    let usedSheets = [];
    let printSheets = [];
    let hasEditElements = false;
    let names = [];
    // if (sizeBefore < 1000000) return data;
    data.names?.forEach((name) => {
      const formula = name?.formula;
      const nameString = name?.name;
      if (formula) {
        const sheetName = getSheetNameFromAddress(formula) ?? "";
        if (sheetName) {
          names.push({
            name: nameString,
            sheetName: sheetName.toLowerCase().replaceAll("'", ""),
          });
        }
      }
    });
    console.log("INFO dataSize before optimization", sizeBefore);
    // let elementInfo = await this.getElementInfoFromJson(data, );

    data.activeSheetIndex = 0;
    data.startSheetIndex = 0;
    // get the sheet object
    let sheets = data.sheets;
    if (elements.length === 0 && sheets.hasOwnProperty("AR_INFO")) {
      let arInfoData = await this.getConfigurationFromArInfo(
        sheets["AR_INFO"],
        elementId,
      );
      if (arInfoData) {
        let configuration = {};
        let calculationInfo = {};
        // add the active configuration
        elements = arInfoData.elementList ?? [];
        calculationInfo["elementList"] = arInfoData.elementList;
        calculationInfo["topImageUrl"] = arInfoData.topImageUrl;
        calculationInfo["caption"] = arInfoData.caption;
        configuration["calculationInfo"] = calculationInfo;
        configuration["elementParameters"] = null;
        data["_configuration"] = configuration;
        // remove the AR_INFO sheet from the cache
        delete sheets["AR_INFO"];
      }
    }
    // remove airrange sheets
    if (sheets.hasOwnProperty("AR_INFO")) delete sheets["AR_INFO"];
    if (sheets.hasOwnProperty("AR_LOGS")) delete sheets["AR_LOGS"];
    // get the used sheets
    elements.forEach((element) => {
      const address = element?.address ?? element?.cell;
      if (address) {
        const sheetName = getSheetNameFromAddress(address)
          .toLowerCase()
          .replaceAll("'", "");
        if (sheetName) {
          usedSheets.push(sheetName);
        } else {
          // is it a named range
          const findIndex = names.findIndex((name) => name.name === address);
          if (findIndex > -1) {
            const sheetName = names[findIndex].sheetName ?? "";
            if (sheetName) usedSheets.push(sheetName);
          }
        }
      }
      // check if it is an editing element
      if (isEditorElement(element)) {
        hasEditElements = true;
      }
      if (element?.element === "ResultChart") {
        let sheetName = element?.sheetName ?? "";
        if (!sheetName) {
          const chartAddress = element?.address;
          if (chartAddress?.includes("{")) {
            try {
              const chartInfo = JSON.parse(chartAddress) ?? {};
              if (chartInfo.hasOwnProperty("worksheet")) {
                sheetName = chartInfo.worksheet ?? "";
              }
            } catch (error) {
              console.log("ERROR reading chartInfo", error);
            }
          }
        }

        if (sheetName) {
          sheetName = sheetName.toLowerCase().replaceAll("'", "");
          element.sheetName = sheetName;
          usedSheets.push(sheetName);
        }
        hasEditElements = true;
      }
      if (element?.element === "ActionButton") {
        const printAction = element.action_print;
        const printSettings = printAction?.split(";");
        if (printSettings?.length === 2) {
          const printSheetName = printSettings[1]
            ?.toLowerCase()
            .replaceAll("'", "");
          if (printSheetName) {
            usedSheets.push(printSheetName);
            printSheets.push(printSheetName);
          }
        }
      }
    });
    let uniqueUsedSheets = [...new Set(usedSheets)];
    // get relevant elements - elements that need information about the sheet
    let relevantElements = elements.filter((element) =>
      ["SpreadTable", "ResultTable", "ResultChart", "TableView"].includes(
        element.element,
      ),
    );
    relevantElements.forEach((element) => {
      const address = element.address;
      const sheetName = getSheetNameFromAddress(address);
      if (sheetName) element.sheetName = sheetName;
    });

    // go through all the sheets
    let sheetCount = 0;
    let options = [];
    Object.keys(sheets).forEach((sheetItem) => {
      sheetCount++;
      let tempSheet = sheets[sheetItem];
      this.optimizeOfflineSheet(
        tempSheet,
        relevantElements,
        uniqueUsedSheets,
        printSheets,
        hasEditElements,
        options,
      );
    });
    data.sheetCount = sheetCount;
    let sizeAfter = getObjectSize(data);
    console.log("INFO size after optimization", sizeAfter);
    // if (sizeAfter < 3000000) return data;
    return data;
  }

  optimizeOfflineSheet(
    sheet,
    elements,
    usedSheets,
    printSheets,
    hasEditElements,
    options = [],
  ) {
    if (!sheet) return;
    console.log("INFO optimizeData: hasEditElements", hasEditElements);
    const sheetName = (sheet.name ?? "").toLowerCase().replaceAll("'", "");
    const isUsedSheet = usedSheets.includes(sheetName);
    // const isPrintSheet = printSheets?.includes(sheetName);
    // objectList.forEach((objectItem) => {
    //   if (sheet.hasOwnProperty(objectItem)) {
    //     delete sheet[objectItem];
    //   }
    // });
    if (!isUsedSheet) {
      if (!hasEditElements) {
        // we don't need to recalculate - remove all unused data
        delete sheet.data;
        if (sheet.hasOwnProperty("pivotTables")) delete sheet.pivotTables;
        if (sheet.hasOwnProperty("tables")) delete sheet.tables;
        if (sheet.hasOwnProperty("names")) delete sheet.names;
        // if (sheet.hasOwnProperty("sharedFormulas")) sheet.sharedFormulas = {};
      }
      if (sheet.hasOwnProperty("selections")) delete sheet.selections;
      if (sheet.hasOwnProperty("rowFilter")) delete sheet.rowFilter;
      if (sheet.hasOwnProperty("charts")) delete sheet.charts;
      if (sheet.hasOwnProperty("printInfo")) delete sheet.printInfo;
      if (sheet.hasOwnProperty("columns")) delete sheet.columns;
      if (sheet.hasOwnProperty("rows")) delete sheet.rows;
      if (sheet.hasOwnProperty("theme")) delete sheet.theme;
      if (sheet.hasOwnProperty("shapes")) delete sheet.shapes;
      if (sheet.hasOwnProperty("floatingObjects")) delete sheet.floatingObjects;
      if (sheet.hasOwnProperty("validations")) sheet.validations = [];
      if (sheet.hasOwnProperty("defaults")) sheet.defaults = {};
      if (sheet.hasOwnProperty("spans")) sheet.spans = [];
      if (sheet.hasOwnProperty("comments")) sheet.comments = [];
    } else {
      // if (!isPrintSheet && sheet.hasOwnProperty("shapes")) delete sheet.shapes;
    }
  }

  async optimizeForService(fileData, options) {
    if (!fileData) return;
    let data = jsonCopy(fileData);
    let sizeAfter;

    try {
      let sizeBefore = getObjectSize(data);
      console.log("INFO size after optimization", sizeBefore);

      data.namedStyles = [];
      // get the sheet object
      let sheets = data.sheets;
      // if (data.docProps) data.docProps.docPropsApp = {};

      // remove airrange sheets
      if (sheets.hasOwnProperty("AR_INFO")) delete sheets["AR_INFO"];
      if (sheets.hasOwnProperty("AR_LOGS")) delete sheets["AR_LOGS"];

      Object.keys(sheets).forEach((sheetItem, sheetIndex) => {
        let tempSheet = sheets[sheetItem];
        if (tempSheet.shapes) tempSheet.shapes = [];
        if (tempSheet.charts) tempSheet.charts = [];
        // if (tempSheet.validations) tempSheet.validations = [];
        if (tempSheet.printInfo) tempSheet.printInfo = {};
        // if (tempSheet.theme) tempSheet.theme = {};
        // if (tempSheet.data.defaultDataNode) tempSheet.data.defaultDataNode = {};
        // clean dataTable styles
        let dataTable = tempSheet.data?.dataTable ?? {};
        Object.keys(dataTable).forEach((row) => {
          const rowData = dataTable[row];
          Object.keys(rowData).forEach((column) => {
            const columnData = rowData[column];
            if (columnData && columnData.style) {
              delete columnData.style;
            }
          });
        });
        // clean dataTable styles
        // let dataArray = tempSheet.data?.columnDataArray;
        // Object.keys(dataArray).forEach((col) => {
        //   const columnData = dataArray[col];
        //   if (columnData && columnData.style) {
        //     delete columnData.style;
        //   }
        // });
      });

      sizeAfter = getObjectSize(data);
      console.log("INFO size after optimization", sizeAfter);
    } catch (error) {
      console.error("Error optimizing for service", error);
    }
    return { data: data, dataSize: sizeAfter };
  }

  // =========================================================
  // CONFIGURATION
  // =========================================================

  async getElementInfoFromCacheData(cacheData, cacheVersion, elementId) {
    if (!cacheData || !elementId) return;

    let dataJson;
    try {
      this.clearLatestCacheDataJson();
      console.time("getElementInfoFromCacheData getDataJson");
      let cacheCompressed = await this.downloadCacheData(
        cacheData,
        cacheVersion,
        true,
      );
      if (!cacheCompressed) {
        console.log("ERROR no compressed data found");
        return;
      }
      // get the detailInfo like calculationInfo for the element
      dataJson = decompressJson(cacheCompressed);
      // store it for ManagerCalculationLocal
      this.latestCacheData = cacheData;
      this.latestCacheDataJson = dataJson;
      console.timeEnd("getElementInfoFromCacheData getDataJson");
    } catch (error) {
      console.log("ERROR getElementInfoFromCacheData downloadCacheData", error);
      return;
    }
    // =================================================
    // try to get the definition from the json package
    // =================================================
    let elementInfo = {};
    let calculationInfo;
    let configuration = dataJson?._configuration;
    if (configuration) {
      console.log("INFO getCalculationInfo from cacheData _configuration");
      if (configuration.elementParameters) {
        elementInfo.elementParameters = configuration.elementParameters;
      }
      calculationInfo = configuration.calculationInfo;
      // =================================================
      // filter elements by license
      // =================================================
      if (this.latestCacheDataUserLicense) {
        calculationInfo.creatorLicense = this.latestCacheDataUserLicense;
        let license = this.latestCacheDataUserLicense;
        if (license === "free" || license === "lite") {
          // mark as branded
          calculationInfo.needsBranding = true;
        }
        if (license === "pro") {
          // remove branding
          calculationInfo.needsBranding = false;
        }
      }
      if (this.latestCacheDataUserId)
        calculationInfo.creatorId = this.latestCacheDataUserId;
    }
    // =================================================
    // try to get the definition from the arInfo-table
    // =================================================
    let arInfoTable = dataJson?.sheets?.AR_INFO?.data?.dataTable;
    if (!calculationInfo && arInfoTable) {
      let calculationInfoString;
      console.log("INFO getCalculationInfo from cached AR_INFO table");

      // collect the definition cell values from the table
      for (const [, value] of Object.entries(arInfoTable)) {
        let definitionId = value[0]?.value;
        if (definitionId === elementId) {
          let def1 = value[1]?.value ?? "";
          let def2 = value[2]?.value ?? "";
          let def3 = value[3]?.value ?? "";
          let def4 = value[4]?.value ?? "";
          let def5 = value[5]?.value ?? "";
          let def6 = value[6]?.value ?? "";
          calculationInfoString =
            def1 + "" + def2 + "" + def3 + "" + def4 + "" + def5 + "" + def6;
        }
      }
      if (!calculationInfoString) {
        console.log("ERROR no calculationInfoString found");
        return;
      }
      try {
        calculationInfo = decompressJson(calculationInfoString) ?? {};
      } catch (error) {
        console.log("ERROR definition error", error);
      }
    } else {
      console.log("INFO no arInfoTable found");
    }

    // get the names list
    let names = [];
    try {
      let jsonNames = dataJson?.names;

      jsonNames &&
        jsonNames.forEach((tempName) => {
          // look for collaboration
          names.push({
            id: tempName.name?.toLowerCase(),
            name: tempName.name,
            address: getOptimizedCellAddress(tempName.formula),
          });
        });
    } catch (error) {
      console.log("ERROR names error", error);
    }

    // a hack for german date formatters
    if (this.rootStore.main.userLanguage?.includes("de")) {
      let namedStyles = dataJson?.namedStyles;
      if (namedStyles) {
        namedStyles.forEach((tempNamedStyle) => {
          if (tempNamedStyle?.formatter === "m/d/yyyy") {
            tempNamedStyle.formatter = "dd.mm.yy";
          }
        });
      }
    }

    // turn the definition into an object
    try {
      // detailInfo = decompressJson(detailInfoString) ?? {};
      if (calculationInfo && names.length > 0) {
        calculationInfo["names"] = names;
      }
    } catch (error) {
      console.log("ERROR definition error", error);
    }
    if (!calculationInfo) return;

    // build the main element object
    elementInfo.calculationInfo = calculationInfo;
    return elementInfo;
  }

  async getConfigurationFromArInfo(arInfoSheet, elementId) {
    if (!arInfoSheet || !elementId) return;

    let calculationInfoString = "";
    let calculationInfo = {};
    let arInfoTable = arInfoSheet.data?.dataTable;
    if (arInfoTable) {
      // collect the definition cell values from the table
      for (const [, value] of Object.entries(arInfoTable)) {
        let definitionId = value[0]?.value;
        if (definitionId === elementId) {
          let def1 = value[1]?.value ?? "";
          let def2 = value[2]?.value ?? "";
          let def3 = value[3]?.value ?? "";
          let def4 = value[4]?.value ?? "";
          let def5 = value[5]?.value ?? "";
          let def6 = value[6]?.value ?? "";
          calculationInfoString =
            def1 + "" + def2 + "" + def3 + "" + def4 + "" + def5 + "" + def6;
        }
      }
      if (!calculationInfoString) {
        console.log("ERROR no calculationInfoString found");
        return;
      }
      try {
        calculationInfo = decompressJson(calculationInfoString) ?? {};
      } catch (error) {
        console.log("ERROR definition error", error);
      }
    } else {
      console.log("INFO no arInfoTable found");
    }
    return calculationInfo;
  }

  // =========================================================
  // AWS CACHE
  // =========================================================

  async getCacheDataUploadUrl(workbookId, elementId, versionId) {
    // check if tenant has its own upload storage

    // use our own cache service
    const uploadUrl = await this.rootStore.appsync.getCacheDataUploadUrl(
      workbookId,
      elementId,
      versionId,
    );
    return uploadUrl;
  }

  async uploadCacheData(jsonData, cacheData, cacheVersion, callback) {
    try {
      let workbookId;
      let elementId;
      let versionId = "-1";

      // get the variables for cache data
      let cacheDataItems = cacheData.split("/");
      if (!cacheDataItems || cacheDataItems.length < 3) return false;

      // optimize the json data
      // let json = await this.optimizeJson(jsonData);

      workbookId = cacheDataItems[1];
      elementId = cacheDataItems[2];
      let saveData = compressJson(jsonData);
      let objectSize = getObjectSize(saveData);
      console.log("INFO appSize", objectSize);

      // delete the old file from the cache
      await this.rootStore.appsync.deleteCacheData(
        workbookId,
        elementId,
        versionId,
      );

      // get a time limited upload url and upload it
      const uploadUrl = await this.getCacheDataUploadUrl(
        workbookId,
        elementId,
        versionId,
      );
      if (!uploadUrl) {
        console.log("ERROR uploadCacheData - no uploadUrl");
        return;
      }

      await axios.put(uploadUrl, saveData);
      // inform ready
      if (callback) callback(uploadUrl);

      return true;
    } catch (err) {
      console.log("ERROR uploadCacheData", err);
      return false;
    }
  }

  async uploadCacheDataToStorage(jsonData, cacheData, cacheVersion, callback) {
    try {
      let publicUserId;
      let workbookId;
      let elementId;
      let versionId = "-1";
      let cacheStorage = null; // perhaps use a customer storage

      // get the variables for cache data
      let cacheDataItems = cacheData.split("/");
      if (!cacheDataItems || cacheDataItems.length < 3) return null;

      // optimize the json data
      // let json = await this.optimizeJson(jsonData);

      publicUserId = cacheDataItems[0];
      workbookId = cacheDataItems[1];
      elementId = cacheDataItems[2];
      const version = this.rootStore.isSafari ? "v2" : null; // no utf16 for safari
      let saveData = compressJson(jsonData, version);
      let objectSize = getObjectSize(saveData);
      console.log("INFO uploadSize", objectSize);

      // get a time limited upload url and upload it
      const uploadUrl =
        await this.rootStore.appsync.getPublicCacheDataUploadUrl(
          publicUserId,
          workbookId,
          elementId,
          versionId,
        );
      if (!uploadUrl) {
        console.log("ERROR uploadCacheDataToStorage - no uploadUrl");
        return null;
      }

      await axios.put(uploadUrl, saveData);
      // inform ready
      if (callback) callback(uploadUrl);

      return cacheStorage;
    } catch (err) {
      console.log("ERROR uploadCacheDataToStorage", err);
      return null;
    }
  }

  async getCacheDataDownloadUrl(tenantId, workbookId, fileId, versionId) {
    // check if the tenant has its own download storage

    // get from our aws S3 bucket
    let downloadUrl = await this.rootStore.appsync.getCacheDataDownloadUrl(
      tenantId,
      workbookId,
      fileId,
      versionId,
    );
    return downloadUrl;
  }

  async downloadCacheDataFile(cacheData) {
    if (!cacheData) return;

    let tenantId;
    let workbookId;
    let fileId;
    let versionId;

    // get the variables for cache data
    let cacheDataItems = cacheData.split("/");
    if (!cacheDataItems || cacheDataItems.length < 3) return false;
    tenantId = cacheDataItems[0];
    workbookId = cacheDataItems[1];
    fileId = cacheDataItems[2];
    versionId = "-1";

    // get the secure download url
    let downloadUrl = await this.getCacheDataDownloadUrl(
      tenantId,
      workbookId,
      fileId,
      versionId,
    );
    if (!downloadUrl) return false;

    // download the data and store compressed in sessionStorage
    try {
      let data = await axios(downloadUrl, {
        // query URL without using browser cache
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
          Expires: "0",
        },
      });
      const fileData = new Blob([data?.data], {
        type: "application/octet-stream",
      });
      const fileName = fileId + versionId + ".dat";
      // save blob to a file
      const url = window.URL.createObjectURL(fileData);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
    } catch (err) {
      console.log("ERROR axios downloadCacheDataFile", err);
    }
  }

  async downloadCacheData(
    cacheData,
    cacheVersion,
    returnData = false,
    ignoreCache = false,
  ) {
    if (!cacheData) return;

    let tenantId;
    let workbookId;
    let fileId;
    let versionId;
    let fileType = "airrange";
    let useCache = true;

    // =====================================
    // check if it is a URL
    // =====================================

    if (cacheData.includes("https://")) {
      fileId = cacheData;
      fileType = "url";
      // no caching for excel-inventory
      if (cacheData.includes("excel-inventory.airrange.io")) useCache = false;
    } else {
      // get the variables for cache data
      let cacheDataItems = cacheData.split("/");
      if (!cacheDataItems || cacheDataItems.length < 3) return false;
      tenantId = cacheDataItems[0];
      workbookId = cacheDataItems[1];
      fileId = cacheDataItems[2];
    }
    versionId = "-1";

    // is it in storage
    let storageKey = "cachedata-" + fileId + "-compressed";
    let storageKeyVersion = "cachedata-" + fileId + "-version";
    let storageKeyInitial = "cachedata-" + fileId + "-initial";
    let storageLastVersion = this.getStorageCacheItem(storageKeyVersion);
    const isVersionOk = storageLastVersion === cacheVersion;
    let cacheAvailable = this.isThereAStorageCacheData(fileId) && isVersionOk;
    if (ignoreCache) cacheAvailable = false;

    // ====================================
    // use the cache
    // ====================================
    if (cacheAvailable && useCache) {
      console.log("INFO cachedData is available");
      if (returnData === true) {
        return this.getStorageCacheItem(storageKey);
      } else return true;
    }
    if (!isVersionOk) console.log("INFO downloading newer cache version");

    // ====================================
    // get the secure download url
    // ====================================
    let downloadUrl;
    if (fileType === "airrange") {
      downloadUrl = await this.getCacheDataDownloadUrl(
        tenantId,
        workbookId,
        fileId,
        versionId,
      );
    } else {
      downloadUrl = cacheData;
    }
    if (!downloadUrl) return false;

    // ====================================
    // download the data
    // ====================================
    try {
      let data;
      let compressedData;
      if (fileType === "airrange") {
        data = await axios(downloadUrl, {
          // query URL without using browser cache
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Expires: "0",
          },
        });
        compressedData = data?.data;
      } else {
        data = await axios(downloadUrl);
        compressedData = data?.data;
        // get the license information
        const userLicense = compressedData?.userLicense;
        const userId = compressedData?.userId;
        if (userLicense) {
          this.latestCacheDataUserLicense = userLicense;
          this.latestCacheDataUserId = userId;
        }
        if (compressedData?.type === "json") {
          const embeddedData = getAsJson(compressedData)?.data;
          const compressedDataString = getAsJson(embeddedData)?.data;
          compressedData = "v3_" + compressedDataString;
        }
      }
      // ====================================
      // store compressed in sessionStorage
      // ====================================
      if (useCache && !ignoreCache) {
        try {
          if (this.useLocalStorageForCache) {
            localStorage.setItem(storageKey, compressedData);
            localStorage.setItem(storageKeyVersion, cacheVersion);
          } else {
            sessionStorage.setItem(storageKey, compressedData);
            sessionStorage.setItem(storageKeyVersion, cacheVersion);
          }
          return returnData === true ? compressedData : true;
        } catch (error) {
          console.log("ERROR storing data to session storage", error);
        }
      } else {
        return returnData === true ? compressedData : true;
      }

      // clear the storage and try again
      try {
        // clear the storage entries
        this.clearCacheDataFromStorage();
        // try to store again
        if (this.useLocalStorageForCache) {
          localStorage.setItem(storageKey, compressedData);
          localStorage.setItem(storageKeyVersion, cacheVersion);
          localStorage.setItem(storageKeyInitial, null); // delete the initial values cache
        } else {
          sessionStorage.setItem(storageKey, compressedData);
          sessionStorage.setItem(storageKeyVersion, cacheVersion);
          sessionStorage.setItem(storageKeyInitial, null); // delete the initial values cache
        }
      } catch (error) {
        console.log("ERROR storing data to session storage", error);
        return false;
      }
    } catch (err) {
      console.log("ERROR axios isLocalDataAvailable", err);
    }
  }

  async deleteCacheData(cacheData, cacheVersion) {
    try {
      let workbookId;
      let fileId;
      let versionId = "-1";

      // get the variables for cache data
      let cacheDataItems = cacheData.split("/");
      if (!cacheDataItems || cacheDataItems.length < 3) return false;
      const tenantId = cacheDataItems[0];

      workbookId = cacheDataItems[1];
      fileId = cacheDataItems[2];
      // delete the old file from the cache
      await this.rootStore.appsync.deleteCacheData(
        workbookId,
        fileId,
        versionId,
        tenantId,
      );
      return true;
    } catch (err) {
      console.log("ERROR deleteCacheData", err);
      return false;
    }
  }

  clearLatestCacheDataJson() {
    this.latestCacheData = null;
    this.latestCacheDataJson = null;
    this.latestCacheDataUserLicense = null;
    this.latestCacheDataUserId = null;
  }
}
